import React, { useState } from "react";
import axios from "axios";
import { useLang } from "../contexts/LanguageContext";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, ArrowRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t, lang } = useLang();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    product_interest: "",
    message: "",
  });

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      toast.success(t.contact.form.success);
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        product_interest: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.form.error);
    } finally {
      setSubmitting(false);
    }
  };

  const products = t.products.list.map((p) => p.name);

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#FDFBF7] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-16 reveal">
          <div className="flex items-center gap-3 mb-6">
            <span className="divider-line" />
            <span className="overline text-[#DE9E36]">{t.contact.overline}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl text-[#1A201C] font-bold leading-[1.1] tracking-tight">
            {t.contact.title}
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#4E5750] leading-relaxed">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Info column */}
          <div className="lg:col-span-5 reveal">
            <div className="bg-[#1A201C] text-white p-8 md:p-10 h-full flex flex-col justify-between">
              <div className="space-y-8">
                <Info
                  icon={<MapPin size={18} strokeWidth={1.5} />}
                  label={t.contact.info.addressLabel}
                  value={t.contact.info.address}
                />
                <Info
                  icon={<Phone size={18} strokeWidth={1.5} />}
                  label={t.contact.info.phoneLabel}
                  value={
                    <div className="space-y-4">
                      <div>
                        <div className="text-[10px] tracking-[0.18em] uppercase text-[#DE9E36]">
                          Secretariat — Iryna Boyko
                        </div>
                        <a href="tel:+380939116649" data-testid="contact-phone-secretariat" className="block hover:text-[#DE9E36]">
                          +380 93 911 6649
                        </a>
                      </div>
                      <div>
                        <div className="text-[10px] tracking-[0.18em] uppercase text-[#DE9E36]">
                          Export — Tatiana Chaban
                        </div>
                        <a href="tel:+380933681038" data-testid="contact-phone-export" className="block hover:text-[#DE9E36]">
                          +380 93 368 1038
                        </a>
                      </div>
                      <div>
                        <div className="text-[10px] tracking-[0.18em] uppercase text-[#DE9E36]">
                          Logistic — Alexandre Katcho
                        </div>
                        <a href="tel:+380947121387" data-testid="contact-phone-logistic" className="block hover:text-[#DE9E36]">
                          +380 94 712 1387
                        </a>
                      </div>
                    </div>
                  }
                />
                <Info
                  icon={<Mail size={18} strokeWidth={1.5} />}
                  label={t.contact.info.emailLabel}
                  value={
                    <div className="space-y-1">
                      <a
                        href="mailto:sekretar@ekobiotek-ukrainellc.com"
                        data-testid="contact-email-secretary"
                        className="block hover:text-[#DE9E36]"
                      >
                        sekretar@ekobiotek-ukrainellc.com
                      </a>
                      <a
                        href="mailto:sekretar@ekobiotek-ukraine.com.ua"
                        data-testid="contact-email-secretary-ua"
                        className="block hover:text-[#DE9E36]"
                      >
                        sekretar@ekobiotek-ukraine.com.ua
                      </a>
                      <a
                        href="mailto:export@ekobiotek-ukrainellc.com"
                        data-testid="contact-email-export"
                        className="block hover:text-[#DE9E36]"
                      >
                        export@ekobiotek-ukrainellc.com
                      </a>
                    </div>
                  }
                />
                <Info
                  icon={<Clock size={18} strokeWidth={1.5} />}
                  label={t.contact.info.scheduleLabel}
                  value={t.contact.info.schedule}
                />
              </div>
              <div className="mt-10 pt-8 border-t border-white/15">
                <div className="text-2xl text-[#DE9E36] font-bold tracking-tight">EKOBIOTEK-UKRAINA, LLC</div>
                <div className="overline text-white/55 mt-2">EDRPOU 32125693 · Since 2003</div>
              </div>
            </div>
          </div>

          {/* Form column */}
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 reveal grid grid-cols-1 sm:grid-cols-2 gap-5 bg-white border border-[#E5E0D8] p-8 md:p-10"
          >
            <Field
              label={t.contact.form.name}
              required
              testId="contact-input-name"
              value={form.name}
              onChange={onChange("name")}
            />
            <Field
              label={t.contact.form.email}
              required
              type="email"
              testId="contact-input-email"
              value={form.email}
              onChange={onChange("email")}
            />
            <Field
              label={t.contact.form.phone}
              testId="contact-input-phone"
              value={form.phone}
              onChange={onChange("phone")}
            />
            <Field
              label={t.contact.form.company}
              testId="contact-input-company"
              value={form.company}
              onChange={onChange("company")}
            />

            <div className="sm:col-span-2 flex flex-col gap-2">
              <label className="overline text-[#4E5750]">{t.contact.form.product}</label>
              <Select
                value={form.product_interest}
                onValueChange={(v) => setForm((f) => ({ ...f, product_interest: v }))}
              >
                <SelectTrigger
                  data-testid="contact-product-select"
                  className="h-12 rounded-none border-[#E5E0D8] bg-[#FDFBF7] text-[#1A201C] focus:ring-[#DE9E36]"
                >
                  <SelectValue placeholder={t.contact.form.productPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-2 flex flex-col gap-2">
              <label className="overline text-[#4E5750]">{t.contact.form.message} *</label>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={onChange("message")}
                data-testid="contact-input-message"
                className="border border-[#E5E0D8] bg-[#FDFBF7] p-4 text-[#1A201C] focus:outline-none focus:border-[#DE9E36] resize-none"
              />
            </div>

            <div className="sm:col-span-2 flex items-center justify-between gap-4 pt-2">
              <span className="text-xs text-[#4E5750]">
                * {t.contact.info.scheduleLabel}: {t.contact.info.schedule}
              </span>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="group inline-flex items-center gap-2 px-7 py-4 bg-[#DE9E36] text-[#1A201C] text-xs tracking-[0.22em] uppercase font-medium hover:bg-[#1A201C] hover:text-[#DE9E36] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? t.contact.form.sending : t.contact.form.submit}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

const Info = ({ icon, label, value }) => (
  <div className="flex items-start gap-4">
    <span className="flex-shrink-0 w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-[#DE9E36]">
      {icon}
    </span>
    <div>
      <div className="overline text-white/55">{label}</div>
      <div className="mt-1 text-sm text-white">{value}</div>
    </div>
  </div>
);

const Field = ({ label, required, type = "text", value, onChange, testId }) => (
  <div className="flex flex-col gap-2">
    <label className="overline text-[#4E5750]">
      {label} {required && "*"}
    </label>
    <input
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      data-testid={testId}
      className="h-12 border border-[#E5E0D8] bg-[#FDFBF7] px-4 text-[#1A201C] focus:outline-none focus:border-[#DE9E36]"
    />
  </div>
);

export default Contact;
